import { useCallback, useState } from 'react'

import { listTeamsForWherebyRerun, rerunWherebyTasksForTeamIds } from '../../../../apiLayer'
import useAppState from '../../../../hooks/useAppState'
import type { WherebyRerunTeamInfo } from '../../../../../../pure-js/types/WherebyRerun'

export interface UseWherebyRerunResult {
  query: string
  setQuery: (q: string) => void
  teams: WherebyRerunTeamInfo[]
  selectedTeamIds: string[]
  toggleTeam: (teamId: string) => void
  isLoading: boolean
  error: string
  result: string
  includeCompleted: boolean
  setIncludeCompleted: (v: boolean) => void
  dryRun: boolean
  setDryRun: (v: boolean) => void
  loadTeams: () => Promise<void>
  runRerun: () => Promise<void>
}

/**
 * State + side effects for the "rerun whereby tasks by team" dev tool.
 * Encapsulates: team list, selection set, run options, and the two async calls.
 */
export function useWherebyRerun(): UseWherebyRerunResult {
  const { state } = useAppState()

  const [query, setQuery] = useState('')
  const [teams, setTeams] = useState<WherebyRerunTeamInfo[]>([])
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState('')
  const [includeCompleted, setIncludeCompleted] = useState(false)
  const [dryRun, setDryRun] = useState(false)

  const loadTeams = useCallback(async () => {
    setIsLoading(true)
    setError('')
    setResult('')
    try {
      const res = await listTeamsForWherebyRerun({ query, limit: 200 }, state)
      if (!res.success) {
        setError(res.error || 'Failed to load teams')
        setTeams([])
        return
      }
      setTeams(res.data.teams || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load teams')
      setTeams([])
    } finally {
      setIsLoading(false)
    }
  }, [state, query])

  const toggleTeam = useCallback((teamId: string) => {
    setSelectedTeamIds((prev) => (prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]))
  }, [])

  const runRerun = useCallback(async () => {
    setIsLoading(true)
    setError('')
    setResult('')
    try {
      const res = await rerunWherebyTasksForTeamIds({ teamIds: selectedTeamIds, dryRun, includeCompleted }, state)
      if (!res.success) {
        setError(res.error || 'Failed to rerun whereby tasks')
        return
      }
      const d = res.data
      setResult(
        `${d.dryRun ? '[Dry run] ' : ''}Reset ${d.totalResetTasks} task(s) across ${d.totalTeams} team(s) (matched: ${d.totalMatchedTasks}, roomNames: ${d.totalRoomNames}).`
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to rerun whereby tasks')
    } finally {
      setIsLoading(false)
    }
  }, [state, selectedTeamIds, dryRun, includeCompleted])

  return {
    query,
    setQuery,
    teams,
    selectedTeamIds,
    toggleTeam,
    isLoading,
    error,
    result,
    includeCompleted,
    setIncludeCompleted,
    dryRun,
    setDryRun,
    loadTeams,
    runRerun
  }
}
